import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations as defaultTranslations } from '../i18n/translations';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const LANGS = ['de', 'en', 'es'];
const LanguageContext = createContext();

// German lives at the root, English under /en, Spanish under /es – one crawlable URL per language.
export const splitLangPath = (pathname) => {
  const m = (pathname || '/').match(/^\/(en|es)(\/.*)?$/);
  return m ? { lang: m[1], path: m[2] || '/' } : { lang: 'de', path: pathname || '/' };
};

export const buildPath = (lang, path) => (lang === 'de' ? path : `/${lang}${path === '/' ? '' : path}`);

// Regional pages exist in German only.
const germanOnly = (path) => path.startsWith('/einsatzgebiete/');

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang: language, path: basePath } = splitLangPath(location.pathname);
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    const stored = localStorage.getItem('itcs-lang');
    if (language === 'de' && (stored === 'en' || stored === 'es') && !basePath.startsWith('/admin') && !germanOnly(basePath)) {
      navigate(buildPath(stored, basePath) + location.search + location.hash, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    axios.get(`${API}/translations`).then(res => setOverrides(res.data || {})).catch(() => {});
  }, []);

  const t = useCallback((key) => {
    if (overrides[key] && overrides[key][language]) return overrides[key][language];
    return defaultTranslations[language]?.[key] || defaultTranslations['de']?.[key] || key;
  }, [language, overrides]);

  const lp = useCallback((path) => buildPath(language, path), [language]);

  const setLanguage = (lang) => {
    if (!LANGS.includes(lang)) return;
    localStorage.setItem('itcs-lang', lang);
    navigate(buildPath(lang, lang !== 'de' && germanOnly(basePath) ? '/' : basePath) + location.search + location.hash);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, lp, basePath }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
